import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { IEdGateStandardCollection } from '@models/lesson-standard';
import { ButtonComponent } from '@shared/button/button.component';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { BehaviorSubject } from 'rxjs';


/**
 * Displays a list of standards rendered recursively into a list.
 *
 * Contains static methods that can be used to help filter through a list of {@link IEdGateStandardCollection }.
 */
@Component({
  selector: 'zbp-branded-standards',
  templateUrl: './branded-standards.component.html',
  styleUrls: ['./branded-standards.component.scss'],
  imports: [CommonModule, ZbpIconComponent, ButtonComponent]
})
export class BrandedStandardsComponent implements OnChanges {
  /**
   * Collection of EdgeGate Standards that will be rendered
   * with their `subStandards` rendered recursively as nested lists.
   */
  @Input() standardCollection: IEdGateStandardCollection;
  /** Title that appears on the Standards button when expanded and collapsed. */
  @Input() title: string = 'Student Objectives';
  /** Will be added to the component container to help with specific styling when adding this component to a page. */
  @Input() customClass: string = null;
  /** Will expand all standard sections when true. */
  @Input() isPrinting: boolean = false;

  /** `BehaviorSubject` for {@link BrandedStandardsComponent.standardCollection} used to update the template.*/
  standardCollection$ = new BehaviorSubject<IEdGateStandardCollection>(null);
  /**
   * If entire component is expanded to show to show the standards data.
   *
   * Default to expanded so student-objectives are visible by default.
   */
  isExpanded: boolean = true;
  /**
   * Tracks which standard is expanded. Entries are removed when user closes a standard's section.
   */
  expandedSections: { [key: string]: boolean } = {};

  readonly arrowChevronUpIconName = zbpIconNames.arrow_chevron_up;
  readonly arrowChevronDownIconName = zbpIconNames.arrow_chevron_down;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.standardCollection && changes.standardCollection.currentValue) {
      this.standardCollection$.next(changes.standardCollection.currentValue);
      // Initialize top-level sections as expanded by default so their
      // chevron shows expanded; deeper children are left collapsed.
      const standards = changes.standardCollection.currentValue?.standards;
      if (standards?.length) {
        standards.forEach((_, idx) => {
          this.expandedSections[`standard_${idx}`] = true;
        });
      }
    }

    if (changes.isPrinting) {
      this.isExpanded = this.isPrinting;
    }
  }

  toggleSection(elementId: string): void {
    const toggleElement = document.getElementById(elementId);

    if (toggleElement) {
      if (toggleElement.classList.contains('show-section')) {
        toggleElement.classList.remove('show-section');
        toggleElement.classList.add('hide-section');
        this.expandedSections[elementId] = false;
      } else {
        toggleElement.classList.add('show-section');
        toggleElement.classList.remove('hide-section');
        this.expandedSections[elementId] = true;
      }
    }
  }

  toggleStudentObjectivesDisplay() {
    this.isExpanded = !this.isExpanded;
    if (!this.isExpanded) {
      this.expandedSections = {};
    } else {
      this.standardCollection$.value?.standards.forEach((_, idx) => {
        this.expandedSections[`standard_${idx}`] = true;
      });
    }
  }

  /**
   * Gets a single `IEdGateStandardCollection` from a list of `IEdGateStandardCollection` entries.
   *
   * Flattens all standards from each entry into a single array of standards in the returned collection.
   *
   * Can be called by components rendering this component.
   *
   * @param {IEdGateStandardCollection[]} lessonStandards List of collections to flatten
   * @returns {IEdGateStandardCollection}
   *  A single collection of all standards flattened into the collections' standards list.
   */
  static getStandards(lessonStandards: IEdGateStandardCollection[]): IEdGateStandardCollection {
    let standards: IEdGateStandardCollection;
    if (lessonStandards?.length) {
      // Get all standards that match the current Tab or that have no section name
      // Some products do not have a standards with a Section Name.
      // Sections without a section name should always be shown.
      if (lessonStandards?.length) {
        [standards] = lessonStandards;
        // Flatten all standards into a single array for the template
        standards.standards = lessonStandards?.flatMap(standard => standard.standards);
      } else {
        standards = null;
      }
    }

    return standards;
  }

  /**
   * Gets a single `IEdGateStandardCollection`
   * from a list of `IEdGateStandardCollection` entries, filtered by `sectionName`.
   * This also returns all collections that do no have a `sectionName` (those should always be shown).
   *
   * Flattens all standards from each matching entry (and sections without names)
   * into a single array of standards in the returned collection.
   *
   * Can be called by components rendering this component.
   *
   * @param {IEdGateStandardCollection[]} lessonStandards List of collections to flatten
   * @param {string} sectionName Used to filter out standards that have section names that do not match
   * @returns {IEdGateStandardCollection}
   *  A single collection of all standards flattened into the collections' standards list.
   */
  static getStandardsBySectionName(
    lessonStandards: IEdGateStandardCollection[],
    sectionName: string
  ): IEdGateStandardCollection {
    let standards: IEdGateStandardCollection;
    // Get all standards that match the current Tab or that have no section name
    // Some products do not have a standards with a Section Name.
    // Standards without a section name should always be shown.
    const matchingStandards = lessonStandards?.filter(s => (s.sectionName === sectionName) || !s.sectionName);
    if (matchingStandards?.length) {
      const [externalStandards] = matchingStandards;
      standards = externalStandards;
      // Flatten all matching standards for lesson into a single list for the template
      standards.standards = matchingStandards?.flatMap(standard => standard.standards);
    } else {
      standards = null;
    }
    return standards;
  }
}
